import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Loader2, Copy, Check, ChevronDown, ArrowLeft,
  FileText, Sparkles, Scale, Layers, TrendingUp, Users,
} from 'lucide-react';
import * as api from '../../services/api';
import MatchHighlighter, { MethodBadge } from './MatchHighlighter';
import HSCode from '../common/HSCode';
import { useTariffTree } from '../../hooks/useTariffTree';

function formatMoney(value) {
  if (value == null || value === '') return null;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return null;
  return `$${num.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}`;
}

function formatWeight(value) {
  if (value == null || value === '') return null;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return `${value} кг`;
  return `${num.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} кг`;
}

function FactRow({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-3 px-3 h-8">
      <span className="text-[10.5px] uppercase tracking-wider text-text-muted">{label}</span>
      <span className={`text-[12px] font-medium text-text-primary text-right truncate ${mono ? 'font-mono' : ''}`} title={String(value)}>
        {value}
      </span>
    </div>
  );
}

function PartyBlock({ role, name, code, addr }) {
  if (!name) return null;
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider text-text-muted">{role}</div>
      <div className="mt-0.5 text-[12px] font-semibold text-text-primary leading-snug">{name}</div>
      {code && <div className="text-[10.5px] font-mono text-text-muted mt-0.5">ЄДРПОУ {code}</div>}
      {addr && <div className="text-[10.5px] text-text-muted leading-snug mt-0.5">{addr}</div>}
    </div>
  );
}

function IconBtn({ title, onClick, tone = 'neutral', active = false, children }) {
  const tones = {
    neutral: 'text-text-muted hover:bg-hover hover:text-text-primary',
    blue: 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40',
    emerald: 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40',
    amber: 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40',
    violet: 'text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-950/40',
  };
  const activeTones = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    violet: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    neutral: 'bg-hover text-text-primary',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${active ? activeTones[tone] : tones[tone]}`}
    >
      {children}
    </button>
  );
}

function AncestorLadder({ path, focusCode, onFocus }) {
  if (!path || path.length === 0) return null;
  const minLevel = Math.min(...path.map(n => n.level || 0));
  return (
    <div className="px-5 mt-2">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Тарифний шлях</div>
      <div className="rounded-lg border border-border-default bg-elevated overflow-hidden divide-y divide-border-default">
        {path.map((node) => {
          const isCurrent = node.code === focusCode;
          const indent = ((node.level || 0) - minLevel) * 12;
          return (
            <button
              key={node.hs_id}
              type="button"
              onClick={() => onFocus?.(node.code)}
              className={`flex w-full items-baseline gap-2 px-2.5 py-1 text-left transition-colors ${
                isCurrent
                  ? 'bg-amber-50 dark:bg-amber-950/30'
                  : 'hover:bg-hover'
              }`}
              title={node.name}
            >
              <span className="shrink-0" style={{ width: indent }} aria-hidden="true" />
              <span className={`shrink-0 font-mono text-[11px] tabular-nums ${
                isCurrent
                  ? 'text-amber-800 dark:text-amber-300 font-semibold'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                {node.code}
              </span>
              <span className={`flex-1 min-w-0 truncate text-[11.5px] leading-snug ${
                isCurrent ? 'text-text-primary font-medium' : 'text-text-secondary'
              }`}>
                {node.name}
              </span>
              {node.duty_display && (
                <span className="shrink-0 ml-1 text-[10px] font-mono tabular-nums text-amber-700 dark:text-amber-400">
                  {node.duty_display}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DetailPanel({
  declaration,
  searchQuery,
  searchMethod,
  onClose,
  onAnalyzeSemantic,
  onAnalyzePractice,
  // tariff rail control
  tariffOpen,
  onToggleTariff,
  focusCode,
  onFocusInTariff,
  // similar rail control
  similarOpen,
  onToggleSimilar,
  // history (open-as-main breadcrumb)
  previousDeclaration,
  onBackToPrevious,
}) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [partsOpen, setPartsOpen] = useState(false);

  const { tree, getNodeByCode, getAncestorPath } = useTariffTree();

  useEffect(() => {
    if (!declaration?.original_id) return;
    setFull(null);
    setLoading(true);
    setPartsOpen(false);
    api.getDeclaration(declaration.original_id)
      .then(setFull)
      .catch(err => console.error('Failed to load declaration:', err))
      .finally(() => setLoading(false));
  }, [declaration?.original_id]);

  const data = full || declaration || {};
  const productCode = data.product_code;

  const originalNode = useMemo(() => {
    if (!tree || !productCode) return null;
    return getNodeByCode(productCode);
  }, [tree, productCode, getNodeByCode]);

  const ancestorPath = useMemo(() => {
    if (!tree || !originalNode) return [];
    return getAncestorPath(originalNode.hs_id);
  }, [tree, originalNode, getAncestorPath]);

  const originalDuty = originalNode?.duty_display || null;
  const originalUnit = originalNode?.unit || null;
  const originalUnitAdditional = originalNode?.unit_additional || null;

  if (!declaration) return null;

  const method = data.search_method || searchMethod || '';
  const priceDisplay = formatMoney(data.customs_value_usd);
  const netWeightDisplay = formatWeight(data.weight_net_kg);
  const grossWeightDisplay = formatWeight(data.weight_gross_kg);
  const declarationDateDisplay = data.declaration_date || data.declaration_year;
  const matchPercent = data.relevance_score != null ? Math.round(data.relevance_score * 100) : null;
  const showMatch = matchPercent != null && method.includes('similar');

  const copyDescription = () => {
    if (data.product_description_original) {
      navigator.clipboard.writeText(data.product_description_original);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <aside className="w-[460px] min-w-[360px] bg-surface border-l border-border-default flex flex-col animate-slide-in-right">

      {/* Breadcrumb: visible only when navigated via "open as main" */}
      {previousDeclaration && onBackToPrevious && (
        <div className="flex items-center gap-2 border-b border-border-default bg-blue-50/40 dark:bg-blue-950/20 px-5 py-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={onBackToPrevious}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 dark:text-blue-300 hover:underline"
            title={`Повернутись до ID ${previousDeclaration.original_id}`}
          >
            <ArrowLeft className="w-3 h-3" />
            Повернутись до{' '}
            <span className="font-mono tabular-nums">{previousDeclaration.original_id}</span>
          </button>
        </div>
      )}

      {/* TOP-ROW: methods + match% + icon actions */}
      <div className="flex items-center justify-between gap-2 px-5 pt-3 pb-1 flex-shrink-0">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          {method && <MethodBadge method={method} />}
          {showMatch && (
            <span className="inline-flex items-center gap-1 text-[10.5px] text-text-muted">
              <span className="font-mono tabular-nums text-violet-600 dark:text-violet-400 font-semibold">
                {matchPercent}%
              </span>
              <span>схожість</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {onToggleTariff && productCode && (
            <IconBtn
              title={tariffOpen ? 'Сховати тарифне дерево' : 'Відкрити тарифне дерево'}
              tone="amber"
              active={tariffOpen}
              onClick={onToggleTariff}
            >
              <Scale className="w-3.5 h-3.5" />
            </IconBtn>
          )}
          {onToggleSimilar && data.original_id && (
            <IconBtn
              title={similarOpen ? 'Сховати схожі товари' : 'Відкрити схожі товари'}
              tone="violet"
              active={similarOpen}
              onClick={onToggleSimilar}
            >
              <Sparkles className="w-3.5 h-3.5" />
            </IconBtn>
          )}
          {onAnalyzeSemantic && productCode && (
            <IconBtn
              title="Цены похожих"
              tone="blue"
              onClick={() => onAnalyzeSemantic({
                anchorId: data.original_id,
                codePrefix: (productCode || '').slice(0, 4),
                fullCode: productCode,
              })}
            >
              <TrendingUp className="w-3.5 h-3.5" />
            </IconBtn>
          )}
          {onAnalyzePractice && productCode && (
            <IconBtn
              title="Практика оформлення"
              tone="emerald"
              onClick={() => onAnalyzePractice({
                anchorId: data.original_id,
                codePrefix: (productCode || '').slice(0, 4),
                fullCode: productCode,
              })}
            >
              <Layers className="w-3.5 h-3.5" />
            </IconBtn>
          )}
          <IconBtn title="Закрити" onClick={onClose}>
            <X className="w-3.5 h-3.5" />
          </IconBtn>
        </div>
      </div>

      {/* HS code + duty + units */}
      <div className="px-5 pt-1 flex items-center gap-2 flex-shrink-0">
        <HSCode code={productCode} size="md" showCopyAlways />
        {originalDuty && (
          <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 h-[20px] text-[11px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50 whitespace-nowrap">
            {originalDuty}
          </span>
        )}
        {(originalUnit || originalUnitAdditional) && (
          <span className="ml-auto text-[10.5px] text-text-muted whitespace-nowrap truncate">
            {originalUnit && <>од: <span className="text-text-secondary">{originalUnit}</span></>}
            {originalUnitAdditional && originalUnitAdditional !== originalUnit && (
              <> · дод: <span className="text-text-secondary">{originalUnitAdditional}</span></>
            )}
          </span>
        )}
      </div>

      {/* Ancestor ladder */}
      <AncestorLadder path={ancestorPath} focusCode={focusCode || productCode} onFocus={onFocusInTariff} />

      {/* TABS — Опис default */}
      <div className="flex items-center gap-5 border-b border-border-default px-5 mt-3 text-[12.5px] flex-shrink-0">
        {[
          { id: 'description', label: 'Опис',   tone: 'text-blue-600 dark:text-blue-400',   count: null, Icon: FileText },
          { id: 'details',     label: 'Деталі', tone: 'text-emerald-600 dark:text-emerald-400', count: null, Icon: Layers },
        ].map(t => {
          const active = activeTab === t.id;
          const TabIcon = t.Icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`relative h-10 inline-flex items-center gap-1.5 font-medium transition-colors ${
                active ? t.tone : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {t.label}
              {t.count != null && t.count > 0 && (
                <span className={`inline-flex h-4 min-w-4 items-center justify-center rounded px-1 text-[10px] font-semibold tabular-nums ${
                  active ? 'bg-current/15' : 'bg-elevated text-text-muted border border-border-default'
                }`}>
                  {t.count}
                </span>
              )}
              {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-t bg-current" />}
            </button>
          );
        })}
      </div>

      {/* CONTENT */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500 dark:text-blue-400" />
          </div>
        ) : (
          <>
            {/* TAB: Description (default) */}
            {activeTab === 'description' && (
              <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3">
                <div className="group/desc relative">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Опис товару</div>
                  <p className="text-[14px] leading-relaxed text-text-primary pr-7">
                    <MatchHighlighter
                      text={data.product_description_original}
                      query={searchQuery}
                      searchMethod={method}
                      productCode={productCode}
                    />
                  </p>
                  <button
                    onClick={copyDescription}
                    className="absolute top-0 right-0 p-1 rounded text-text-disabled hover:bg-hover hover:text-text-secondary opacity-0 group-hover/desc:opacity-100 transition-opacity"
                    title="Копіювати опис"
                  >
                    {copied
                      ? <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                      : <Copy className="w-3.5 h-3.5" />
                    }
                  </button>
                </div>
              </div>
            )}

            {/* TAB: Details */}
            {activeTab === 'details' && (
              <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3 space-y-3">
                {(data.trademark || data.country_of_origin || priceDisplay || netWeightDisplay || grossWeightDisplay || declarationDateDisplay || data.trading_country) && (
                  <div className="rounded-lg border border-border-default bg-elevated divide-y divide-border-default overflow-hidden">
                    <FactRow label="Марка" value={data.trademark} />
                    <FactRow label="Країна" value={data.country_of_origin} />
                    <FactRow label="Торг. кр." value={data.trading_country} />
                    <FactRow label="Дата" value={declarationDateDisplay} />
                    <FactRow label="Вартість" value={priceDisplay} />
                    <FactRow label="Вага нетто" value={netWeightDisplay} />
                    <FactRow label="Вага брутто" value={grossWeightDisplay} />
                  </div>
                )}

                <div className="rounded-lg border border-border-default bg-elevated divide-y divide-border-default overflow-hidden">
                  <FactRow label="ID запису" value={data.original_id} mono />
                  <FactRow label="№ декларації" value={data.declaration_number} mono />
                </div>

                {(data.importer_name || data.exporter_name || data.declarant_name) && (
                  <div className="rounded-lg border border-border-default bg-elevated overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setPartsOpen(o => !o)}
                      className="flex w-full items-center gap-2 px-3 h-9 hover:bg-hover/50 transition-colors"
                      aria-expanded={partsOpen}
                    >
                      <Users className="w-3.5 h-3.5 text-text-muted shrink-0" />
                      <span className="text-[11.5px] font-medium text-text-secondary">Учасники угоди</span>
                      <ChevronDown className={`ml-auto w-3.5 h-3.5 text-text-muted transition-transform ${partsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {partsOpen && (
                      <div className="px-3 pb-3 pt-2 space-y-2.5 border-t border-border-default">
                        <PartyBlock role="Імпортер" name={data.importer_name} code={data.importer_code} addr={data.importer_address} />
                        <PartyBlock role="Експортер" name={data.exporter_name} addr={data.exporter_address} />
                        <PartyBlock role="Декларант" name={data.declarant_name} code={data.declarant_code} addr={data.declarant_address} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
